import Notifier from 'common/helpers/Notifier';
import { useInvalidateQueries, useUpdateRecord, useCreateRecord } from 'src/api/APIHooks';
import { isHttpSuccess } from 'src/api/utils/httpStatus';
import { REQUEST_UNASSIGNED } from 'common/Workqueue/hooks/useAssigneeOptions';
import { getOriginalId } from '../utils/serviceAuthorizationUtils';

export const useUpdateServiceAuthorization = (onErrorCallback) => {
  const invalidateQueries = useInvalidateQueries();
  const { updateRecord } = useUpdateRecord('service_authorization', {
    mutationConfig: {
      onError: (error) => {
        if (onErrorCallback) {
          onErrorCallback(error);
        } else {
          const errorMessage = error?.response?.data?.errors?.[0]?.title || 'Something went wrong.';
          Notifier.dispatch('error', errorMessage);
        }
      },
    },
  });

  return async (id, updatedFields, successMessage) => {
    const updateFieldsFormatted = {
      ...updatedFields,
      assignee: updatedFields.assignee === REQUEST_UNASSIGNED ? null : updatedFields.assignee,
    };
    const updateResponse = await updateRecord(id, updateFieldsFormatted);
    if (isHttpSuccess(updateResponse.status)) {
      Notifier.dispatch(updateResponse.status, successMessage);
    }
    invalidateQueries('service_authorization');
    invalidateQueries('authorized_spend');
    invalidateQueries('case');
    invalidateQueries(`service_authorizations/${id}/timeline_event`);
  };
};

export const useAcceptServiceAuthorization = () => {
  const update = useUpdateServiceAuthorization();

  return async ({
    authorization,
    reviewer,
    reviewer_note,
    payer_authorization_number,
    approved_cents,
    approved_unit_amount,
    approved_starts_at,
    approved_ends_at,
    clinical_modifications,
  }) => {
    const updateFields = {
      state: 'approved',
      reviewer,
      approved_cents,
      approved_unit_amount,
      approved_starts_at,
      approved_ends_at,
      ...(reviewer_note && { reviewer_note }),
      ...(payer_authorization_number && { payer_authorization_number }),
    };

    if (clinical_modifications?.[0]?.id) {
      updateFields.clinical_modifications = clinical_modifications;
    }

    return update(
      authorization.id,
      updateFields,
      `Authorization Request #${authorization.short_id} has been approved and accepted!`,
    );
  };
};

export const useRejectServiceAuthorization = () => {
  const update = useUpdateServiceAuthorization();

  return async ({
    authorization,
    reviewer,
    denial_reason,
    reviewer_note,
  }) => update(
    authorization.id,
    {
      state: 'denied',
      reviewer,
      service_authorization_denial_reason: denial_reason,
      ...(reviewer_note && { reviewer_note }),
    },
    'Service Authorization Rejected.',
  );
};

export const useReviewServiceAuthorization = () => {
  const update = useUpdateServiceAuthorization();

  return async ({
    authorization,
    service_authorization_review_reason,
    in_review_note,
    assignee,
  }) => {
    const authState = authorization.state;
    const updateFields = {
      ...(authState !== 'in_review' && { state: 'in_review' }),
      service_authorization_review_reason,
      in_review_note: in_review_note || null,
      assignee,
    };

    return update(
      authorization.id,
      updateFields,
      authState !== 'in_review' ? 'Request placed in review' : 'Review details saved',
    );
  };
};

export const useCreateRequestedServiceAuthorizationEdition = () => {
  const invalidateQueries = useInvalidateQueries();

  const { createRecord } = useCreateRecord('service_authorization', {
    mutationConfig: {
      onSuccess: (response) => {
        Notifier.dispatch(response.status, 'Authorization update request submitted successfully');
        invalidateQueries('service_authorization');
        invalidateQueries('case');
        invalidateQueries('authorized_spend');
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.errors?.[0]?.title || 'Something went wrong.';
        Notifier.dispatch('error', errorMessage);
      },
    },
  });

  return async ({
    authorization,
    requester,
    update_request_note,
    requested_cents,
    requested_unit_amount,
    requested_starts_at,
    requested_ends_at,
  }) => {
    const createFields = {
      state: 'requested',
      requester,
      original_service_authorization: getOriginalId(authorization),
      requested_starts_at,
      requested_ends_at,
      requested_cents,
      requested_unit_amount,
      update_request_note,
    };

    return createRecord(createFields);
  };
};

export const useCreateApprovedServiceAuthorizationEdition = () => {
  const invalidateQueries = useInvalidateQueries();

  const { createRecord } = useCreateRecord('service_authorization', {
    mutationConfig: {
      onSuccess: (response) => {
        Notifier.dispatch(response.status, 'Authorization successfully updated');
        invalidateQueries('service_authorization');
        invalidateQueries('case');
        invalidateQueries('authorized_spend');
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.errors?.[0]?.title || 'Something went wrong.';
        Notifier.dispatch('error', errorMessage);
      },
    },
  });

  return async ({
    authorization,
    reviewer,
    requester,
    reviewer_note,
    payer_authorization_number,
    approved_cents,
    approved_unit_amount,
    service_authorization_edit_reason,
    approved_starts_at,
    approved_ends_at,
    clinical_modifications,
  }) => {
    const createFields = {
      state: 'approved',
      reviewer,
      requester,
      original_service_authorization: getOriginalId(authorization),
      approved_starts_at,
      approved_ends_at,
      requested_starts_at: approved_starts_at,
      requested_ends_at: approved_ends_at,
      approved_cents,
      approved_unit_amount,
      requested_cents: approved_cents,
      requested_unit_amount: approved_unit_amount,
      ...(service_authorization_edit_reason?.id && { service_authorization_edit_reason }),
      ...(reviewer_note && { reviewer_note }),
      ...(payer_authorization_number && { payer_authorization_number }),
    };

    if (clinical_modifications?.[0]?.id) {
      createFields.clinical_modifications = clinical_modifications;
    }

    return createRecord(createFields);
  };
};
